import {
    Component,
    Input,
    OnDestroy,
    OnInit,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { Router } from "@angular/router";
import { ButtonComponent } from "@components/button/button.component";
import { firstValueFrom } from "rxjs";
import { BusyService } from "~app/common/components/busy/busy.service";
import { ModalSettings } from "~app/common/components/modal/modal-models";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { ConfirmDialogResponse, RaskRoller, VirkesorderStatus } from "~app/common/models/constants";
import { AuthenticationService } from "~app/common/services/authentication/authentication.service";
import { CommandService } from "~app/common/services/cqs/command.service";
import { ArbetsorderDisabledFlag } from "~app/features/planera/model/ArbetsorderDisabledFlag";
import { IArbetsorderDetail } from "~app/features/planera/model/IArbetsorderDetail";
import { MakuleraArbetsorderCommand } from "../commands/MakuleraArbetsorderCommand";
import { ArbetsorderMonitor, ArbetsorderMonitorService } from "../services/arbetsorder-monitor.service";
import { Produktionsstatusar } from "../services/arbetsorder.service";

@Component({
    selector: "rask-makulera-arbetsorder-button",
    templateUrl: "./makulera-arbetsorder-button.component.html",
    styleUrls: ["./makulera-arbetsorder-button.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [ButtonComponent]
})
export class MakuleraArbetsorderButtonComponent implements OnInit, OnDestroy {
    @Input() arbetsorder: IArbetsorderDetail;
    isSendInProgress = false;
    workMonitor: ArbetsorderMonitor;

    private readonly arbetsorderMonitorService = inject(ArbetsorderMonitorService);
    private readonly authenticationService = inject(AuthenticationService);
    private readonly busyService = inject(BusyService);
    private readonly commandService = inject(CommandService);
    private readonly raskModalService = inject(RaskModalService);
    private readonly router = inject(Router);

    get canMakulera(): boolean {
        if (this.isSendInProgress) {
            return false;
        }

        if (!this.authenticationService.hasRoles(RaskRoller.role.Produktionsleda)) {
            return false;
        }

        const ejAvslutadVo = this.arbetsorder.virkesorderStatus
            .some(x => x.status === VirkesorderStatus.Aktiv ||
                x.status === VirkesorderStatus.FelLoadTender ||
                x.status === VirkesorderStatus.Sand) ||
            this.arbetsorder.skickadTillViol3;

        const makuleradVo = this.arbetsorder.virkesorderStatus.some(x => x.status === VirkesorderStatus.Makulerad) || (this.arbetsorder.viol3
            && this.arbetsorder.skickadTillViol);

        return this.arbetsorder.internProduktionsstatus === Produktionsstatusar.produktionEjPaborjad &&
            this.arbetsorder.isEditableForMode(ArbetsorderDisabledFlag.makulerad) &&
            !ejAvslutadVo &&
            makuleradVo;
    }

    makulera() {
        const settings = new ModalSettings("Makulera arbetsorder",
            `Arbetsordern kommer att makuleras och kvarvarande åtgärder kommer flyttas till ny arbetsorder.
            Justerade utbytessiffror kommer att försvinna. Tryck Makulera arbetsorder eller Avbryt`);
        settings.yesCaption = "Makulera arbetsorder";
        settings.noCaption = "Avbryt";
        firstValueFrom(this.raskModalService.confirm(settings))
            .then((response) => {
                if (response === ConfirmDialogResponse.save) {
                    const command = new MakuleraArbetsorderCommand((x) => {
                        x.arbetsorderId = this.arbetsorder.id;
                    });

                    // Detta command körs alltid isolerat - oavsett vad som ligger i stacken hos commandService
                    const local = this.commandService.createInstance();
                    local.addTransactionCommand(command);

                    this.busyService
                        .addWork(local.pushCommandQueueTransaction())
                        .subscribe(() => {
                            this.router.navigate([
                                "planera",
                                { id: this.arbetsorder.id }
                            ]);
                        });
                }
            });
    }

    ngOnDestroy(): void {
        this.workMonitor.disconnect();
    }

    ngOnInit(): void {
        this.workMonitor = this.arbetsorderMonitorService.connect();
        this.workMonitor.getWork("viol2.skicka").subscribe(x => this.isSendInProgress = x);
    }
}
